/**
 * Session Timeout Calculation Module
 * 
 * Pure function that calculates whether a session should be logged out
 * based on activity, active run status, and security boundaries.
 * 
 * This module is intentionally side-effect-free (pure functions only)
 * to enable unit testing without mocking.
 * 
 * @module sessionTimeout
 * @see Plan 052 Phase 4: Activity-Aware Timeout Logic
 */

import { SESSION_SECURITY_CONSTANTS } from './sessionConfig';

/**
 * Input parameters for idle status calculation
 */
export interface IdleCheckParams {
  /** Timestamp of last user interaction */
  lastActivityTime: number;
  /** Whether an active run is in progress */
  activeRunInProgress: boolean;
  /** Configured idle timeout in milliseconds */
  idleTimeoutMs: number;
  /** Timestamp when the session started (for max duration check) */
  sessionStartTime: number;
}

/**
 * Result of idle status calculation
 */
export interface IdleCheckResult {
  /** Whether the user is considered idle (exceeded timeout) */
  isIdle: boolean;
  /** Whether the session should be terminated */
  shouldLogout: boolean;
  /** Reason for logout decision */
  reason?: 'idle_timeout' | 'max_session_exceeded';
  /** Duration of inactivity in milliseconds */
  idleDurationMs: number;
  /** Duration of session in milliseconds */
  sessionDurationMs: number;
}

/**
 * Calculate whether a session should be logged out.
 * 
 * Security priority order:
 * 1. 8-hour max session (ALWAYS checked first, unconditionally)
 * 2. Active run exemption (exempt from idle timeout only, not from max session)
 * 3. Idle timeout (config-driven, minimum 1 hour enforced in sessionConfig)
 * 
 * @param params - Current session state
 * @returns IdleCheckResult with logout decision and reason
 */
export function calculateIdleStatus(params: IdleCheckParams): IdleCheckResult {
  const now = Date.now();
  const idleDurationMs = now - params.lastActivityTime;
  const sessionDurationMs = now - params.sessionStartTime;

  // 1. SECURITY: Check 8-hour maximum FIRST (unconditional, even for active runs)
  if (sessionDurationMs > SESSION_SECURITY_CONSTANTS.MAX_SESSION_DURATION_MS) {
    return {
      isIdle: true,
      shouldLogout: true,
      reason: 'max_session_exceeded',
      idleDurationMs,
      sessionDurationMs,
    };
  }

  // 2. Active run exemption (only applies to idle timeout, NOT to max session)
  if (params.activeRunInProgress) {
    return {
      isIdle: false,
      shouldLogout: false,
      idleDurationMs,
      sessionDurationMs,
    };
  }

  // 3. Idle timeout check
  if (idleDurationMs > params.idleTimeoutMs) {
    return {
      isIdle: true,
      shouldLogout: true,
      reason: 'idle_timeout',
      idleDurationMs,
      sessionDurationMs,
    };
  }

  // User is active, session is valid
  return {
    isIdle: false,
    shouldLogout: false,
    idleDurationMs,
    sessionDurationMs,
  };
}
